import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge, Button, Card, Col, DatePicker, Divider, Drawer, Form, Input,
  InputNumber, message, Modal, Popconfirm, Radio, Row, Select, Space,
  Switch, Table, Tabs, Tag, Typography,
} from 'antd';
import {
  DeleteOutlined, EditOutlined, PlusOutlined, FileTextOutlined,
  EnvironmentOutlined, ScheduleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import useAuthStore from '../store/authStore';
import api from '../api/client';
import ContractorsTab from '../components/ecology/ContractorsTab';
import {
  EcologyWasteType, EcologyWasteTypePayload, EcologyDisposalRecord,
  EcologyDisposalPayload, EcologyLicense, EcologyLicensePayload,
  LEGAL_ENTITY_LABELS, LegalEntity,
  listWasteTypes, getWasteType, createWasteType, updateWasteType, deleteWasteType,
  listDisposals, createDisposal, updateDisposal, deleteDisposal,
  listLicenses, createLicense, updateLicense, deleteLicense,
} from '../api/ecology';

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const PASSPORT_STATUS_MAP: Record<string, { label: string; color: string }> = {
  present: { label: 'Присутствует', color: 'success' },
  absent: { label: 'Отсутствует', color: 'error' },
  pending: { label: 'Будет сделан', color: 'warning' },
};

const HAZARD_CLASS_COLORS: Record<number, string> = {
  1: 'red',
  2: 'volcano',
  3: 'orange',
  4: 'gold',
  5: 'green',
};

const EcologyPage: React.FC = () => {
  const { hasPermission } = useAuthStore();
  const canWrite = hasPermission('ecology:write');
  const navigate = useNavigate();

  // ── Contracts (scanned documents) for linking ─────────────────
  const [contractDocs, setContractDocs] = useState<{ id: number; title: string }[]>([]);

  // ── Filters ─────────────────────────────────────────────────────
  const [legalEntity, setLegalEntity] = useState<LegalEntity>('autoregium');
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'waste' | 'licenses' | 'contractors'>('waste');

  // ── Data ────────────────────────────────────────────────────────
  const [items, setItems] = useState<EcologyWasteType[]>([]);
  const [loading, setLoading] = useState(false);
  const [licenses, setLicenses] = useState<EcologyLicense[]>([]);
  const [licensesLoading, setLicensesLoading] = useState(false);

  // ── Drawer ──────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerId, setDrawerId] = useState<number | null>(null);
  const [drawerData, setDrawerData] = useState<EcologyWasteType | null>(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerTab, setDrawerTab] = useState('info');

  // ── Disposals in drawer ─────────────────────────────────────────
  const [disposals, setDisposals] = useState<EcologyDisposalRecord[]>([]);
  const [disposalsLoading, setDisposalsLoading] = useState(false);
  const [disposalModalOpen, setDisposalModalOpen] = useState(false);
  const [editingDisposal, setEditingDisposal] = useState<EcologyDisposalRecord | null>(null);
  const [disposalForm] = Form.useForm();

  // ── Waste form modal ────────────────────────────────────────────
  const [wasteModalOpen, setWasteModalOpen] = useState(false);
  const [editingWaste, setEditingWaste] = useState<EcologyWasteType | null>(null);
  const [wasteForm] = Form.useForm();

  // ── License form modal ──────────────────────────────────────────
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState<EcologyLicense | null>(null);
  const [licenseForm] = Form.useForm();

  const loadItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listWasteTypes({
        legal_entity: legalEntity,
        is_active: showInactive ? undefined : true,
        search: search || undefined,
      });
      setItems(data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить отходы');
    } finally {
      setLoading(false);
    }
  }, [legalEntity, showInactive, search]);

  const loadLicenses = useCallback(async () => {
    setLicensesLoading(true);
    try {
      const data = await listLicenses({ legal_entity: legalEntity });
      setLicenses(data);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить лицензии');
    } finally {
      setLicensesLoading(false);
    }
  }, [legalEntity]);

  useEffect(() => {
    if (activeTab === 'waste') loadItems();
    else loadLicenses();
  }, [activeTab, loadItems, loadLicenses]);

  const openDrawer = async (id: number) => {
    setDrawerId(id);
    setDrawerOpen(true);
    setDrawerLoading(true);
    setDrawerTab('info');
    try {
      const data = await getWasteType(id);
      setDrawerData(data);
      // load disposals
      setDisposalsLoading(true);
      const d = await listDisposals(id);
      setDisposals(d);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось загрузить карточку');
    } finally {
      setDrawerLoading(false);
      setDisposalsLoading(false);
    }
  };

  const refreshDrawer = async () => {
    if (!drawerId) return;
    setDrawerLoading(true);
    try {
      const data = await getWasteType(drawerId);
      setDrawerData(data);
      const d = await listDisposals(drawerId);
      setDisposals(d);
      loadItems();
    } finally {
      setDrawerLoading(false);
    }
  };

  // ── Waste CRUD ──────────────────────────────────────────────────
  const loadContractDocs = useCallback(async () => {
    if (contractDocs.length) return;
    try {
      const res = await api.get('/documents');
      setContractDocs(res.data.map((d: any) => ({ id: d.id, title: d.title || `Документ #${d.id}` })));
    } catch {
      message.error('Не удалось загрузить список документов');
    }
  }, [contractDocs.length]);

  const openWasteModal = (item?: EcologyWasteType) => {
    loadContractDocs();
    setEditingWaste(item || null);
    if (item) {
      wasteForm.setFieldsValue({
        ...item,
        passport_date: item.passport_date ? dayjs(item.passport_date) : null,
      });
    } else {
      wasteForm.resetFields();
      wasteForm.setFieldsValue({ legal_entity: legalEntity, is_active: true, passport_status: 'absent' });
    }
    setWasteModalOpen(true);
  };

  const handleWasteSubmit = async () => {
    try {
      const values = await wasteForm.validateFields();
      const payload: EcologyWasteTypePayload = {
        ...values,
        passport_date: values.passport_date ? values.passport_date.format('YYYY-MM-DD') : null,
      };
      if (editingWaste) {
        await updateWasteType(editingWaste.id, payload);
        message.success('Отход обновлён');
      } else {
        await createWasteType(payload);
        message.success('Отход добавлен');
      }
      setWasteModalOpen(false);
      loadItems();
      if (drawerId && editingWaste?.id === drawerId) refreshDrawer();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    }
  };

  const handleDeleteWaste = async (id: number) => {
    try {
      await deleteWasteType(id);
      message.success('Отход удалён');
      loadItems();
      if (drawerId === id) setDrawerOpen(false);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  // ── Disposal CRUD ───────────────────────────────────────────────
  const openDisposalModal = (record?: EcologyDisposalRecord) => {
    setEditingDisposal(record || null);
    if (record) {
      disposalForm.setFieldsValue({
        ...record,
        disposal_date: dayjs(record.disposal_date),
      });
    } else {
      disposalForm.resetFields();
      disposalForm.setFieldsValue({
        legal_entity: drawerData?.legal_entity || legalEntity,
        unit: 'кг',
        disposal_date: dayjs(),
      });
    }
    setDisposalModalOpen(true);
  };

  const handleDisposalSubmit = async () => {
    if (!drawerId) return;
    try {
      const values = await disposalForm.validateFields();
      const payload: EcologyDisposalPayload = {
        ...values,
        waste_type_id: drawerId,
        disposal_date: values.disposal_date.format('YYYY-MM-DD'),
        cost: values.cost || null,
      };
      if (editingDisposal) {
        await updateDisposal(editingDisposal.id, payload);
        message.success('Запись о вывозе обновлена');
      } else {
        await createDisposal(drawerId, payload);
        message.success('Запись о вывозе добавлена');
      }
      setDisposalModalOpen(false);
      refreshDrawer();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    }
  };

  const handleDeleteDisposal = async (id: number) => {
    try {
      await deleteDisposal(id);
      message.success('Запись удалена');
      refreshDrawer();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  // ── License CRUD ────────────────────────────────────────────────
  const openLicenseModal = (item?: EcologyLicense) => {
    setEditingLicense(item || null);
    if (item) {
      licenseForm.setFieldsValue({
        ...item,
        issue_date: item.issue_date ? dayjs(item.issue_date) : null,
        expiry_date: item.expiry_date ? dayjs(item.expiry_date) : null,
      });
    } else {
      licenseForm.resetFields();
      licenseForm.setFieldsValue({ legal_entity: legalEntity, is_active: true });
    }
    setLicenseModalOpen(true);
  };

  const handleLicenseSubmit = async () => {
    try {
      const values = await licenseForm.validateFields();
      const payload: EcologyLicensePayload = {
        ...values,
        issue_date: values.issue_date ? values.issue_date.format('YYYY-MM-DD') : null,
        expiry_date: values.expiry_date ? values.expiry_date.format('YYYY-MM-DD') : null,
      };
      if (editingLicense) {
        await updateLicense(editingLicense.id, payload);
        message.success('Лицензия обновлена');
      } else {
        await createLicense(payload);
        message.success('Лицензия добавлена');
      }
      setLicenseModalOpen(false);
      loadLicenses();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || 'Не удалось сохранить');
    }
  };

  const handleDeleteLicense = async (id: number) => {
    try {
      await deleteLicense(id);
      message.success('Лицензия удалена');
      loadLicenses();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || 'Не удалось удалить');
    }
  };

  // ── Columns ─────────────────────────────────────────────────────
  const wasteColumns = useMemo(() => [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: EcologyWasteType) => (
        <Space>
          {!record.is_active && <Tag>Архив</Tag>}
          <Text>{text}</Text>
        </Space>
      ),
    },
    {
      title: 'Код ФККО',
      dataIndex: 'fkko_code',
      key: 'fkko_code',
      width: 160,
    },
    {
      title: 'Класс',
      dataIndex: 'hazard_class',
      key: 'hazard_class',
      width: 80,
      align: 'center' as const,
      render: (cls: number) => cls ? <Tag color={HAZARD_CLASS_COLORS[cls] || 'default'}>{cls}</Tag> : '—',
    },
    {
      title: 'Паспорт',
      dataIndex: 'passport_status',
      key: 'passport_status',
      width: 120,
      render: (status: string) => {
        const s = PASSPORT_STATUS_MAP[status] || { label: status, color: 'default' };
        return <Badge status={s.color as any} text={s.label} />;
      },
    },
    {
      title: 'Подрядчик',
      dataIndex: 'contractor_name',
      key: 'contractor_name',
      width: 120,
      render: (v: string) => v || '—',
    },
    {
      title: 'Периодичность',
      dataIndex: 'disposal_periodicity',
      key: 'disposal_periodicity',
      width: 150,
      render: (v: string) => v || '—',
    },
    {
      title: 'Вывозов',
      dataIndex: 'disposal_count',
      key: 'disposal_count',
      width: 90,
      align: 'center' as const,
    },
    ...(canWrite ? [{
      title: '',
      key: 'actions',
      width: 80,
      render: (_: any, record: EcologyWasteType) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={(e) => { e.stopPropagation(); openWasteModal(record); }} />
          <Popconfirm title="Удалить?" onConfirm={(e) => { e?.stopPropagation(); handleDeleteWaste(record.id); }}>
            <Button icon={<DeleteOutlined />} size="small" danger onClick={(e) => e.stopPropagation()} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ], [canWrite]);

  const licenseColumns = useMemo(() => [
    { title: 'Номер лицензии', dataIndex: 'license_number', key: 'license_number' },
    { title: 'Кем выдана', dataIndex: 'issued_by', key: 'issued_by', render: (v: string) => v || '—' },
    {
      title: 'Срок действия',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: (date: string, record: EcologyLicense) => {
        if (!date) return '—';
        const days = record.days_until_expiry;
        let color = 'green';
        if (days == null) color = 'default';
        else if (days < 0) color = 'red';
        else if (days < 30) color = 'red';
        else if (days < 90) color = 'orange';
        return (
          <Space>
            <Text>{dayjs(date).format('DD.MM.YYYY')}</Text>
            {days != null && (
              <Tag color={color}>{days < 0 ? `Просрочена ${Math.abs(days)} дн.` : `${days} дн.`}</Tag>
            )}
          </Space>
        );
      },
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (v: boolean) => v ? <Tag color="success">Активна</Tag> : <Tag>Неактивна</Tag>,
    },
    ...(canWrite ? [{
      title: '',
      key: 'actions',
      width: 80,
      render: (_: any, record: EcologyLicense) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={(e) => { e.stopPropagation(); openLicenseModal(record); }} />
          <Popconfirm title="Удалить?" onConfirm={() => handleDeleteLicense(record.id)}>
            <Button icon={<DeleteOutlined />} size="small" danger onClick={(e) => e.stopPropagation()} />
          </Popconfirm>
        </Space>
      ),
    }] : []),
  ], [canWrite]);

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>Экология</Title>
        </Col>
        <Col>
          <Space>
            {canWrite && activeTab === 'waste' && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openWasteModal()}>
                Добавить отход
              </Button>
            )}
            {canWrite && activeTab === 'licenses' && (
              <Button type="primary" icon={<PlusOutlined />} onClick={() => openLicenseModal()}>
                Добавить лицензию
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Radio.Group
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="waste">
              <FileTextOutlined /> Отходы
            </Radio.Button>
            <Radio.Button value="contractors">
              <EnvironmentOutlined /> Подрядчики
            </Radio.Button>
            <Radio.Button value="licenses">
              <ScheduleOutlined /> Лицензии
            </Radio.Button>
          </Radio.Group>
        </Col>
        <Col xs={24} md={12}>
          <Space wrap>
            <Radio.Group value={legalEntity} onChange={(e) => setLegalEntity(e.target.value)}>
              <Radio.Button value="autoregium">{LEGAL_ENTITY_LABELS.autoregium}</Radio.Button>
              <Radio.Button value="fomin">{LEGAL_ENTITY_LABELS.fomin}</Radio.Button>
            </Radio.Group>
            {activeTab === 'waste' && (
              <>
                <Input.Search
                  placeholder="Поиск..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onSearch={loadItems}
                  style={{ width: 200 }}
                  allowClear
                />
                <Switch
                  checked={showInactive}
                  onChange={setShowInactive}
                  checkedChildren="Архив"
                  unCheckedChildren="Активные"
                />
              </>
            )}
          </Space>
        </Col>
      </Row>

      {activeTab === 'waste' && (
        <Table
          columns={wasteColumns}
          dataSource={items}
          rowKey="id"
          loading={loading}
          pagination={false}
          onRow={(record) => ({
            onClick: () => openDrawer(record.id),
            style: { cursor: 'pointer' },
          })}
          size="small"
        />
      )}
      {activeTab === 'licenses' && (
        <Table
          columns={licenseColumns}
          dataSource={licenses}
          rowKey="id"
          loading={licensesLoading}
          pagination={false}
          size="small"
        />
      )}
      {activeTab === 'contractors' && <ContractorsTab />}

      {/* ── Drawer: Waste Detail ───────────────────────────────── */}
      <Drawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={720}
        title={drawerData?.name || 'Карточка отхода'}
        loading={drawerLoading}
      >
        {drawerData && (
          <Tabs activeKey={drawerTab} onChange={setDrawerTab}>
            <TabPane tab="Информация" key="info">
              <Descriptions data={drawerData} onOpenContract={(id) => navigate(`/documents/${id}`)} />
              {canWrite && (
                <Button icon={<EditOutlined />} onClick={() => openWasteModal(drawerData)} style={{ marginTop: 16 }}>
                  Редактировать
                </Button>
              )}
            </TabPane>
            <TabPane tab={`Вывозы (${disposals.length})`} key="disposals">
              {canWrite && (
                <Button type="primary" icon={<PlusOutlined />} onClick={() => openDisposalModal()} style={{ marginBottom: 12 }}>
                  Добавить вывоз
                </Button>
              )}
              <Table
                dataSource={disposals}
                rowKey="id"
                loading={disposalsLoading}
                pagination={false}
                size="small"
                columns={[
                  { title: 'Дата', dataIndex: 'disposal_date', render: (d: string) => dayjs(d).format('DD.MM.YYYY') },
                  { title: 'Кол-во', dataIndex: 'quantity', render: (v: number, r: EcologyDisposalRecord) => `${v} ${r.unit}` },
                  { title: 'Стоимость', dataIndex: 'cost', render: (v: number) => v ? `${v} ₽` : '—' },
                  { title: 'Подрядчик', dataIndex: 'contractor_name', render: (v: string) => v || '—' },
                  { title: 'Документ', dataIndex: 'document_number', render: (v: string) => v || '—' },
                  ...(canWrite ? [{
                    title: '',
                    key: 'actions',
                    width: 80,
                    render: (_: any, record: EcologyDisposalRecord) => (
                      <Space>
                        <Button icon={<EditOutlined />} size="small" onClick={() => openDisposalModal(record)} />
                        <Popconfirm title="Удалить?" onConfirm={() => handleDeleteDisposal(record.id)}>
                          <Button icon={<DeleteOutlined />} size="small" danger />
                        </Popconfirm>
                      </Space>
                    ),
                  }] : []),
                ]}
              />
            </TabPane>
            <TabPane tab="Инструкции" key="instructions">
              <Text style={{ whiteSpace: 'pre-wrap' }}>
                {drawerData.collection_instructions || 'Инструкции не заполнены'}
              </Text>
            </TabPane>
          </Tabs>
        )}
      </Drawer>

      {/* ── Modal: Waste Form ──────────────────────────────────── */}
      <Modal
        open={wasteModalOpen}
        title={editingWaste ? 'Редактировать отход' : 'Добавить отход'}
        onCancel={() => setWasteModalOpen(false)}
        onOk={handleWasteSubmit}
        okText="Сохранить"
        cancelText="Отмена"
        width={640}
      >
        <Form form={wasteForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="legal_entity" label="Юрлицо" rules={[{ required: true }]}>
                <Select options={[{ value: 'autoregium', label: LEGAL_ENTITY_LABELS.autoregium }, { value: 'fomin', label: LEGAL_ENTITY_LABELS.fomin }]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Статус" valuePropName="checked">
                <Switch checkedChildren="Активен" unCheckedChildren="Архив" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="name" label="Название" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Наименование (по ФККО)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="fkko_code" label="Код ФККО">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="hazard_class" label="Класс опасности">
                <Select allowClear options={[
                  { value: 1, label: '1 — Чрезвычайно опасные' },
                  { value: 2, label: '2 — Высоко опасные' },
                  { value: 3, label: '3 — Умеренно опасные' },
                  { value: 4, label: '4 — Малоопасные' },
                  { value: 5, label: '5 — Практически неопасные' },
                ]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="passport_status" label="Паспорт">
                <Select options={[
                  { value: 'present', label: 'Присутствует' },
                  { value: 'absent', label: 'Отсутствует' },
                  { value: 'pending', label: 'Будет сделан' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="passport_date" label="Дата паспорта">
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="contractor_name" label="Кто вывозит">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="contract_document_id" label="Договор (скан из «Документов»)">
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Не привязан"
                  options={contractDocs.map((d) => ({ value: d.id, label: d.title }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="disposal_periodicity" label="Периодичность вывоза">
                <Input placeholder="Напр: Март, Июнь, Сентябрь" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="container_type" label="Тип емкости">
            <Input placeholder="Бочка, мешок, ведро..." />
          </Form.Item>
          <Form.Item name="container_location" label="Место емкости">
            <Input placeholder="Кузовной цех, пост масла..." />
          </Form.Item>
          <Form.Item name="collection_instructions" label="Инструкции по сбору">
            <Input.TextArea rows={4} placeholder="Описание процесса сбора и подготовки..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Modal: Disposal Form ───────────────────────────────── */}
      <Modal
        open={disposalModalOpen}
        title={editingDisposal ? 'Редактировать вывоз' : 'Добавить вывоз'}
        onCancel={() => setDisposalModalOpen(false)}
        onOk={handleDisposalSubmit}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={disposalForm} layout="vertical">
          <Form.Item name="legal_entity" label="Юрлицо" rules={[{ required: true }]}>
            <Select options={[{ value: 'autoregium', label: LEGAL_ENTITY_LABELS.autoregium }, { value: 'fomin', label: LEGAL_ENTITY_LABELS.fomin }]} />
          </Form.Item>
          <Form.Item name="disposal_date" label="Дата вывоза" rules={[{ required: true }]}>
            <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="quantity" label="Количество" rules={[{ required: true }]}>
                <InputNumber style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="unit" label="Единица" rules={[{ required: true }]}>
                <Select options={[{ value: 'кг' }, { value: 'т' }, { value: 'л' }, { value: 'м³' }, { value: 'шт' }]} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="cost" label="Стоимость вывоза (₽)">
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
          <Form.Item name="contractor_name" label="Подрядчик">
            <Input />
          </Form.Item>
          <Form.Item name="document_number" label="Номер документа (акт/накладная)">
            <Input />
          </Form.Item>
          <Form.Item name="notes" label="Примечания">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Modal: License Form ────────────────────────────────── */}
      <Modal
        open={licenseModalOpen}
        title={editingLicense ? 'Редактировать лицензию' : 'Добавить лицензию'}
        onCancel={() => setLicenseModalOpen(false)}
        onOk={handleLicenseSubmit}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={licenseForm} layout="vertical">
          <Form.Item name="legal_entity" label="Юрлицо" rules={[{ required: true }]}>
            <Select options={[{ value: 'autoregium', label: LEGAL_ENTITY_LABELS.autoregium }, { value: 'fomin', label: LEGAL_ENTITY_LABELS.fomin }]} />
          </Form.Item>
          <Form.Item name="license_number" label="Номер лицензии" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="issued_by" label="Кем выдана">
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="issue_date" label="Дата выдачи">
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="expiry_date" label="Срок действия до">
                <DatePicker format="DD.MM.YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="is_active" label="Активна" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

// ── Helper: Descriptions ────────────────────────────────────────
const Descriptions: React.FC<{ data: EcologyWasteType; onOpenContract?: (id: number) => void }> = ({ data, onOpenContract }) => (
  <>
    {data.description && (
      <>
        <Text type="secondary">Полное наименование (по паспорту):</Text>
        <div style={{ background: '#f0f5ff', padding: 12, borderRadius: 4, marginBottom: 16 }}>
          <Text style={{ whiteSpace: 'pre-wrap' }}>{data.description}</Text>
        </div>
      </>
    )}
    <Row gutter={[16, 8]}>
      <Col span={12}><Text type="secondary">Юрлицо:</Text><br/><Text>{LEGAL_ENTITY_LABELS[data.legal_entity as keyof typeof LEGAL_ENTITY_LABELS] || data.legal_entity}</Text></Col>
      <Col span={12}><Text type="secondary">Код ФККО:</Text><br/><Text>{data.fkko_code || '—'}</Text></Col>
      <Col span={12}><Text type="secondary">Класс опасности:</Text><br/>{data.hazard_class ? <Tag color={HAZARD_CLASS_COLORS[data.hazard_class]}>{data.hazard_class}</Tag> : '—'}</Col>
      <Col span={12}><Text type="secondary">Паспорт:</Text><br/>{(() => {
        const s = PASSPORT_STATUS_MAP[data.passport_status] || { label: data.passport_status, color: 'default' };
        return <Badge status={s.color as any} text={s.label} />;
      })()}</Col>
      {data.passport_date && (
        <Col span={12}><Text type="secondary">Дата паспорта:</Text><br/><Text>{dayjs(data.passport_date).format('DD.MM.YYYY')}</Text></Col>
      )}
      <Col span={12}><Text type="secondary">Подрядчик:</Text><br/><Text>{data.contractor_name || '—'}</Text></Col>
      <Col span={12}>
        <Text type="secondary">Договор:</Text><br/>
        {data.contract_document_id ? (
          <Button
            type="link"
            size="small"
            icon={<FileTextOutlined />}
            style={{ padding: 0 }}
            onClick={() => onOpenContract?.(data.contract_document_id!)}
          >
            Открыть скан договора #{data.contract_document_id}
          </Button>
        ) : <Text>—</Text>}
      </Col>
      <Col span={12}><Text type="secondary">Периодичность:</Text><br/><Text>{data.disposal_periodicity || '—'}</Text></Col>
      <Col span={12}><Text type="secondary">Емкость:</Text><br/><Text>{data.container_type || '—'}</Text></Col>
      <Col span={12}><Text type="secondary">Место:</Text><br/><Text>{data.container_location || '—'}</Text></Col>
    </Row>
    <Divider />
    <Text type="secondary">Инструкции по сбору:</Text>
    <div style={{ background: '#f6f6f6', padding: 12, borderRadius: 4, marginTop: 8 }}>
      <Text style={{ whiteSpace: 'pre-wrap' }}>{data.collection_instructions || 'Не заполнено'}</Text>
    </div>
  </>
);

export default EcologyPage;
